/**
 * PrecursorBarrierTab Component
 *
 * Deep-dive analysis of the selected fault tree node: its metadata, mapped-report
 * stats, the reports table, and the AI (Chat + CFR) panel. The node's timeline
 * sparkline + map live in the slim top bar (PrecursorBarControls). The node is
 * chosen by navigation (e.g. the dendrogram eye icon), not an in-tab dropdown.
 *
 * Fetches BFF node tab data on selectedNodeId / primaryFilter change and applies
 * secondaryDateFilter client-side to the mapped report list.
 */

import React, {useEffect, useMemo} from 'react';
import {Box, Chip, Divider, Stack, Typography} from '@mui/material';
import {
    useEventSequenceData,
    useIRPWorkbookStore,
    useNodeTabCache,
    usePrimaryFilter,
    useSecondaryDateFilter,
    useSelectedNodeId,
} from '../../../../../../../stores/irp-workbook.store';
import {FaultTreeNode as ImportedFaultTreeNode} from '../../../../../../../@types/faultTreeNode';
import {IrpMappedReportDTO} from '../../../../../../../@types/integrated-risk-picture';
import {
    ConfidenceDistribution, EMPTY_NODE_MAPPING_STATISTICS, NodeMappingStatistics, ReportSourceBreakdown, TimelineData,
} from '../../../../../../../@types/irp-node-mappings';
import {useIRPContext} from '../../IRPWorkbookProvider';
import {toast} from 'react-toastify';
import NodeStatsSummary from '../NodeStatsSummary';
import JustificationAnalysisPanel from '../JustificationAnalysisPanel';
import NodeInteractionsPanel from '../NodeInteractionsPanel';
import FullscreenWrapper from '../../../../../../common/FullscreenWrapper';

/**
 * Precursor/Control Review tab for deep-dive node analysis
 */
const PrecursorBarrierTab: React.FC = () => {
    const irpId = useIRPWorkbookStore((state) => state.irpId);
    const {dataSource} = useIRPContext();
    const selectedNodeId = useSelectedNodeId();
    const setNodeTabCache = useIRPWorkbookStore((state) => state.setNodeTabCache);
    const eventSequenceData = useEventSequenceData();
    const nodeTabCache = useNodeTabCache();
    const primaryFilter = usePrimaryFilter();
    const secondaryDateFilter = useSecondaryDateFilter();

    // Fetch node tab data on selectedNodeId / primaryFilter change / cache clear
    useEffect(() => {
        if (!irpId || !dataSource || !selectedNodeId) return;

        // Use reactive nodeTabCache so the effect re-fires when setIrpId clears it.
        if (nodeTabCache !== null && nodeTabCache.nodeId === selectedNodeId) return;

        const { dateFrom, dateTo, selectedDescriptors, selectedSources } = primaryFilter;
        const dti = selectedDescriptors.size > 0 ? Array.from(selectedDescriptors) : undefined;
        const sn = selectedSources.size > 0 ? Array.from(selectedSources) : undefined;

        dataSource.getWorkbookNodeTab(selectedNodeId, dateFrom, dateTo, dti, sn)
            .then((data) => setNodeTabCache(selectedNodeId, data))
            .catch(() => {
                toast.error('Failed to load node mappings.');
            });
    }, [irpId, dataSource, selectedNodeId, primaryFilter, nodeTabCache, setNodeTabCache]);

    // Loading state — stale when nodeTabCache.nodeId !== selectedNodeId
    const isLoading = !!selectedNodeId && (nodeTabCache === null || nodeTabCache.nodeId !== selectedNodeId);

    // Apply secondaryDateFilter client-side to mapped reports
    const filteredMappedReports = useMemo((): IrpMappedReportDTO[] => {
        const rawReports = nodeTabCache?.mappedReports;
        const raw = isLoading ? [] : (Array.isArray(rawReports) ? rawReports : []);
        if (!secondaryDateFilter?.dateFrom && !secondaryDateFilter?.dateTo) return raw;
        return raw.filter((r) => {
            if (!r.dateTime) return true;
            const date = new Date(r.dateTime);
            if (secondaryDateFilter?.dateFrom && date < secondaryDateFilter.dateFrom) return false;
            if (secondaryDateFilter?.dateTo && date > secondaryDateFilter.dateTo) return false;
            return true;
        });
    }, [nodeTabCache, isLoading, secondaryDateFilter]);

    // Compute statistics client-side from filteredMappedReports
    const statistics = useMemo((): NodeMappingStatistics => {
        const reports = filteredMappedReports;
        const total = reports.length;
        if (total === 0) return EMPTY_NODE_MAPPING_STATISTICS;

        const scores = reports.map((r) => r.confidenceScore).sort((a, b) => a - b);
        const avgConf = scores.reduce((sum, s) => sum + s, 0) / total;
        const medConf = total % 2 === 0
            ? (scores[total / 2 - 1] + scores[total / 2]) / 2
            : scores[Math.floor(total / 2)];

        const manualCount = reports.filter((r) => r.mappingSource === 'MANUAL').length;
        const aiCount = total - manualCount;

        const dist: ConfidenceDistribution = {
            'below70': 0, '70-75': 0, '75-80': 0, '80-85': 0, '85-90': 0, '90-95': 0, '95-100': 0,
        };
        reports.forEach((r) => {
            const pct = r.confidenceScore * 100;
            if (pct < 70) dist.below70++;
            else if (pct < 75) dist['70-75']++;
            else if (pct < 80) dist['75-80']++;
            else if (pct < 85) dist['80-85']++;
            else if (pct < 90) dist['85-90']++;
            else if (pct < 95) dist['90-95']++;
            else dist['95-100']++;
        });

        const sourceBreakdown: ReportSourceBreakdown = {};
        reports.forEach((r) => {
            sourceBreakdown[r.sourceName] = (sourceBreakdown[r.sourceName] || 0) + 1;
        });

        const dateTimes = reports
            .map((r) => r.dateTime ? new Date(r.dateTime) : null)
            .filter((d): d is Date => d !== null);
        const earliest = dateTimes.length > 0 ? new Date(Math.min(...dateTimes.map((d) => d.getTime()))) : null;
        const latest = dateTimes.length > 0 ? new Date(Math.max(...dateTimes.map((d) => d.getTime()))) : null;
        const rangeDays = earliest && latest
            ? Math.round((latest.getTime() - earliest.getTime()) / 86400000)
            : 0;

        const timelineData: TimelineData = {};
        reports.forEach((r) => {
            if (r.dateTime) {
                const d = new Date(r.dateTime);
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (!timelineData[key]) {
                    timelineData[key] = { count: 0, aiCount: 0, manualCount: 0, averageConfidence: 0 };
                }
                timelineData[key].count++;
                if (r.mappingSource === 'MANUAL') {
                    timelineData[key].manualCount++;
                } else {
                    timelineData[key].aiCount++;
                }
                timelineData[key].averageConfidence += r.confidenceScore;
            }
        });
        Object.values(timelineData).forEach((bucket) => {
            bucket.averageConfidence = bucket.averageConfidence / bucket.count;
        });

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentPeriodCount = reports.filter(
            (r) => r.dateTime && new Date(r.dateTime).getFullYear() === currentYear
        ).length;
        const previousPeriodCount = reports.filter(
            (r) => r.dateTime && new Date(r.dateTime).getFullYear() === currentYear - 1
        ).length;
        const pctChange = previousPeriodCount > 0
            ? ((currentPeriodCount - previousPeriodCount) / previousPeriodCount) * 100
            : 0;
        const trendDir: 'up' | 'down' | 'stable' = pctChange > 5 ? 'up' : pctChange < -5 ? 'down' : 'stable';

        return {
            totalReports: total,
            averageConfidence: avgConf,
            medianConfidence: medConf,
            aiMappingsCount: aiCount,
            manualMappingsCount: manualCount,
            confidenceDistribution: dist,
            trendData: { currentPeriodCount, previousPeriodCount, percentageChange: pctChange, trendDirection: trendDir },
            sourceBreakdown,
            dateRangeStats: { earliestDate: earliest, latestDate: latest, dateRangeInDays: rangeDays, timelineData },
        };
    }, [filteredMappedReports]);

    // Extract all nodes from fault tree, used only to resolve the selected node.
    const availableNodes = useMemo((): ImportedFaultTreeNode[] => {
        const initiatingEvent = eventSequenceData?.events?.[0];
        const faultTree = initiatingEvent?.faultTree;
        const faultTreeNodes: ImportedFaultTreeNode[] = faultTree?.faultTreeNodes || [];
        return faultTreeNodes;
    }, [eventSequenceData]);

    // Find the currently selected node (selectedNodeId is number | null)
    const selectedNode = useMemo(() => {
        if (!selectedNodeId) return null;
        return availableNodes.find(node => node.id === selectedNodeId);
    }, [selectedNodeId, availableNodes]);

    if (!selectedNode) {
        return (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: '0.875rem', px: 2, textAlign: 'center' }}>
                Select a node from the fault tree to view its precursor/control analysis
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Node header: name + ID + type chips + stats, then description / statement of truth */}
            <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, mr: 0.5 }}>
                        {selectedNode.name || 'Unnamed node'}
                    </Typography>

                    {/* Node ID */}
                    <Chip
                        label={selectedNode.uniqueId || `node-${selectedNode.id}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                    />

                    {/* Node Type */}
                    <Chip
                        label={selectedNode.type || 'Unknown'}
                        size="small"
                        color="default"
                        sx={{ fontSize: '0.7rem' }}
                    />

                    {/* Node Class if different from type */}
                    {selectedNode.nodeClass && selectedNode.nodeClass !== selectedNode.type && (
                        <Chip
                            label={selectedNode.nodeClass}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    )}

                    {/* Probability if available */}
                    {selectedNode.probability > 0 && (
                        <Chip
                            label={`P: ${selectedNode.probability.toExponential(2)}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                        />
                    )}

                    <Divider orientation="vertical" flexItem sx={{ height: 24 }} />

                    {/* Statistics (reports / AI / Manual) */}
                    <NodeStatsSummary
                        statistics={statistics}
                        totalFilteredReports={filteredMappedReports.length}
                        loading={isLoading}
                    />
                </Box>

                {/* Description and Statement of Truth */}
                {(selectedNode.description || selectedNode.statementOfTruth) && (
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {selectedNode.description && (
                            <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 0.5 }}>
                                    Description:
                                </Typography>
                                {selectedNode.description}
                            </Typography>
                        )}
                        {selectedNode.statementOfTruth && (
                            <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4, fontStyle: 'italic' }}>
                                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'normal', mr: 0.5 }}>
                                    Statement of Truth:
                                </Typography>
                                "{selectedNode.statementOfTruth}"
                            </Typography>
                        )}
                    </Stack>
                )}
            </Box>

            {/* Main content: reports table (2/3) + AI panel (1/3) */}
            <Box sx={{ flex: 1, minHeight: 0, px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, overflow: 'hidden' }}>
                <Box sx={{ flex: 2, minWidth: 0, minHeight: 0 }}>
                    <NodeInteractionsPanel
                        reports={filteredMappedReports}
                        loading={isLoading}
                    />
                </Box>
                <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper'
                }}>
                    <FullscreenWrapper fillParent>
                        <JustificationAnalysisPanel
                            reports={filteredMappedReports}
                            node={selectedNode}
                            loading={isLoading}
                            fillHeight
                        />
                    </FullscreenWrapper>
                </Box>
            </Box>
        </Box>
    );
};

export default PrecursorBarrierTab;
